import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import defaultLogoSrc from "assets/TMS-NepalCan-Logo-Quotation.png";
import internetMailRedIconSrc from "assets/internetMailRedIcon.png";

// ─── Constants ─────────────────────────────────────────────────────────────────

const FOOTER_RESERVE = 65; // pts from bottom edge — content must not enter this zone
const HEADER_BOTTOM_Y = 100; // y where body content starts after the header

// ─── Helpers ───────────────────────────────────────────────────────────────────

const toOrdinalDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "long" });
  const year = date.getFullYear();
  const suffixes = ["th", "st", "nd", "rd"];
  const v = day % 100;
  const suffix = suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0];
  return `${day}${suffix} ${month} ${year}`;
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// For bundled assets (relative paths) fetch directly.
// For external URLs (stored DO Spaces attachments) route through the backend
// proxy to avoid CORS — the browser's fetch() is blocked on cross-origin
// requests to DO Spaces, but <img src> is not (which is why the form preview
// shows the logo but a direct fetch() fails silently).
const urlToDataUrl = (url) => {
  const fetchUrl = url.startsWith("http")
    ? `/api/proxy/download?url=${encodeURIComponent(url)}`
    : url;

  return fetch(fetchUrl)
    .then((res) => {
      if (!res.ok) throw new Error(`Failed to fetch: ${url}`);
      return res.blob();
    })
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        }),
    );
};

const svgPathToDataUrl = (pathD, renderPx = 128, fillColor = "#4a4a4a") =>
  new Promise((resolve) => {
    const svgStr = [
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"`,
      `     width="${renderPx}" height="${renderPx}">`,
      `  <path fill="${fillColor}" d="${pathD}"/>`,
      `</svg>`,
    ].join("\n");

    const blob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
    const blobUrl = URL.createObjectURL(blob);
    const img = new window.Image();

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = renderPx;
      canvas.height = renderPx;
      canvas.getContext("2d").drawImage(img, 0, 0, renderPx, renderPx);
      URL.revokeObjectURL(blobUrl);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      resolve(null);
    };
    img.src = blobUrl;
  });

/** MUI <LocationOn /> */
const MUI_LOCATION_ON_PATH =
  "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" +
  "m0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z";

/** MUI <Phone /> */
const MUI_PHONE_PATH =
  "M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24" +
  " 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17" +
  " 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z";

// ─── Header renderer (called once per page) ────────────────────────────────────

const drawHeader = (doc, formData, logoDataUrl, pageWidth, margin) => {
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, "PNG", margin, 14, 130, 52);
  }

  const rightX = pageWidth - margin;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text(formData.headerCompanyName || "", rightX, 24, { align: "right" });

  doc.setFont("helvetica", "italic");
  doc.setFontSize(8.5);
  doc.text(formData.headerFormerName || "", rightX, 37, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.text(formData.headerRegnNo || "", rightX, 49, { align: "right" });
  doc.text(formData.headerPanNo || "", rightX, 61, { align: "right" });

  doc.setDrawColor(192, 57, 43);
  doc.setLineWidth(1.5);
  doc.line(margin, 76, pageWidth - margin, 76);
};

// ─── Main export ───────────────────────────────────────────────────────────────

export const generateVendorQuotationPdf = async (formData) => {
  const doc = new jsPDF("p", "pt", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;
  const contentWidth = pageWidth - margin * 2;

  // ── Load logo ──────────────────────────────────────────────────────────────
  // Priority: new File selected by user → stored URL from DB → bundled default
  let logoDataUrl = null;
  try {
    if (formData.logoFile instanceof File) {
      logoDataUrl = await fileToDataUrl(formData.logoFile);
    } else if (formData.logo?.url) {
      logoDataUrl = await urlToDataUrl(formData.logo.url);
    } else {
      logoDataUrl = await urlToDataUrl(defaultLogoSrc);
    }
  } catch (e) {
    console.warn("Logo could not be loaded:", e);
  }

  // ── Load footer icons in parallel ─────────────────────────────────────────
  const [locationIconDataUrl, phoneIconDataUrl, mailIconDataUrl] =
    await Promise.all([
      svgPathToDataUrl(MUI_LOCATION_ON_PATH, 128, "#4a4a4a").catch(() => null),
      svgPathToDataUrl(MUI_PHONE_PATH, 128, "#4a4a4a").catch(() => null),
      urlToDataUrl(internetMailRedIconSrc).catch(() => null),
    ]);

  // ── Footer renderer (absolute position — safe to call on any page) ────────
  const drawFooter = () => {
    const fy = pageHeight - 38;

    const LEFT_ICON_SZ = 18;
    const CENTER_ICON_SZ = 14;
    const RIGHT_ICON_SZ = 18;
    const ICON_GAP = 3;

    const twoLineCY = fy + 5.5;
    const oneLineCY = fy + 5;

    doc.setDrawColor(192, 57, 43);
    doc.setLineWidth(0.5);
    doc.line(margin, fy - 10, pageWidth - margin, fy - 10);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(0, 0, 0);

    // Left
    if (locationIconDataUrl) {
      doc.addImage(
        locationIconDataUrl,
        "PNG",
        margin,
        twoLineCY - LEFT_ICON_SZ / 2,
        LEFT_ICON_SZ,
        LEFT_ICON_SZ,
      );
    }
    const leftTextX = margin + LEFT_ICON_SZ + ICON_GAP;
    const leftLines = (formData.footerLeft || "").split("\n");
    doc.text(leftLines[0] || "", leftTextX, fy);
    if (leftLines[1]) doc.text(leftLines[1], leftTextX, fy + 11);

    // Center
    const centreText = formData.footerCenter || "";
    const centreTextW = doc.getTextWidth(centreText);
    const totalW = CENTER_ICON_SZ + ICON_GAP + centreTextW;
    const unitStartX = pageWidth / 2 - totalW / 2;
    if (phoneIconDataUrl) {
      doc.addImage(
        phoneIconDataUrl,
        "PNG",
        unitStartX,
        oneLineCY - CENTER_ICON_SZ / 2,
        CENTER_ICON_SZ,
        CENTER_ICON_SZ,
      );
    }
    doc.text(centreText, unitStartX + CENTER_ICON_SZ + ICON_GAP, fy + 5);

    // Right
    const webText = formData.footerRightWeb || "";
    const emailText = formData.footerRightEmail || "";
    const maxTextW = Math.max(
      doc.getTextWidth(webText),
      doc.getTextWidth(emailText),
    );
    const rightEdge = pageWidth - margin;
    const mailIconX = rightEdge - maxTextW - ICON_GAP - RIGHT_ICON_SZ;
    if (mailIconDataUrl) {
      doc.addImage(
        mailIconDataUrl,
        "PNG",
        mailIconX,
        twoLineCY - RIGHT_ICON_SZ / 2,
        RIGHT_ICON_SZ,
        RIGHT_ICON_SZ,
      );
    }
    doc.text(webText, rightEdge, fy, { align: "right" });
    doc.text(emailText, rightEdge, fy + 11, { align: "right" });
  };

  // ── Page break helper ──────────────────────────────────────────────────────
  // Call before drawing any block. Pass the estimated height of that block.
  // If it won't fit, closes current page and opens a new one with header/footer.
  // Returns the y position to use for drawing (either unchanged or reset to
  // HEADER_BOTTOM_Y on the new page).
  const checkPageBreak = (y, neededHeight) => {
    if (y + neededHeight > pageHeight - FOOTER_RESERVE) {
      drawFooter();
      doc.addPage();
      drawHeader(doc, formData, logoDataUrl, pageWidth, margin);
      return HEADER_BOTTOM_Y;
    }
    return y;
  };

  // ══════════════ PAGE 1 HEADER ══════════════════════════════════════════════
  drawHeader(doc, formData, logoDataUrl, pageWidth, margin);

  let y = HEADER_BOTTOM_Y;

  // ══════════════ DATE ════════════════════════════════════════════════════════
  y = checkPageBreak(y, 22);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(192, 57, 43);
  doc.text(toOrdinalDate(formData.date), margin, y);
  y += 22;

  // ══════════════ TO SECTION ══════════════════════════════════════════════════
  y = checkPageBreak(y, 60);
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("To", margin, y);
  y += 16;

  doc.setFont("helvetica", "bold");
  doc.text(formData.toName || "", margin, y);
  y += 16;
  doc.text(formData.toAddress || "", margin, y);
  y += 28;

  // ══════════════ SUBJECT ════════════════════════════════════════════════════
  y = checkPageBreak(y, 30);
  const subjectStr = `Subject: ${formData.subject || ""}.`;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  const subjectW = doc.getTextWidth(subjectStr);
  const subjectX = (pageWidth - subjectW) / 2;
  doc.text(subjectStr, subjectX, y);
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.5);
  doc.line(subjectX, y + 2, subjectX + subjectW, y + 2);
  y += 22;

  // ══════════════ GREETING ════════════════════════════════════════════════════
  y = checkPageBreak(y, 40);
  doc.text("Dear Sir/Ma'am,", margin, y);
  y += 15;

  const introText =
    "We are pleased to submit our quotation for your transportation requirements as detailed below:";
  const introLines = doc.splitTextToSize(introText, contentWidth - 18);
  y = checkPageBreak(y, introLines.length * 13 + 8);
  doc.text(introLines, margin + 18, y);
  y += introLines.length * 13 + 8;

  // ══════════════ RATE TABLE ═══════════════════════════════════════════════════
  const tableBody = [];
  (formData.tableGroups || []).forEach((group) => {
    const rowCount = group.rows?.length || 0;
    if (rowCount === 0) return;

    group.rows.forEach((row, idx) => {
      if (idx === 0) {
        tableBody.push([
          {
            content: group.description || "",
            rowSpan: rowCount,
            styles: { valign: "middle", halign: "center" },
          },
          {
            content: group.from?.name || "",
            rowSpan: rowCount,
            styles: { valign: "middle", halign: "center" },
          },
          { content: row.to?.name || "", styles: { halign: "center" } },
          { content: row.ratesPerKg || "", styles: { halign: "center" } },
          {
            content: group.remarks || "",
            rowSpan: rowCount,
            styles: { valign: "middle", halign: "center" },
          },
        ]);
      } else {
        tableBody.push([
          { content: row.to?.name || "", styles: { halign: "center" } },
          { content: row.ratesPerKg || "", styles: { halign: "center" } },
        ]);
      }
    });
  });

  autoTable(doc, {
    startY: y,
    head: [["Description", "From", "To", "Rates/Kg", "Remarks"]],
    body: tableBody,
    headStyles: {
      fillColor: [192, 57, 43],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "center",
      fontSize: 9,
    },
    styles: { fillColor: [255, 255, 255] },
    bodyStyles: {
      fillColor: [255, 255, 255],
      fontSize: 9,
      textColor: [0, 0, 0],
      lineColor: [200, 200, 200],
      lineWidth: 0.3,
    },
    alternateRowStyles: { fillColor: [255, 255, 255] },
    columnStyles: {
      0: { cellWidth: 110, halign: "center" },
      1: { cellWidth: 65, halign: "center" },
      2: { cellWidth: 105, halign: "center" },
      3: { cellWidth: 65, halign: "center" },
      4: { cellWidth: "auto", halign: "center" },
    },
    // top: HEADER_BOTTOM_Y tells autoTable where to start content on pages 2+,
    // keeping rows clear of the header that didDrawPage draws above it.
    margin: {
      top: HEADER_BOTTOM_Y,
      left: margin,
      right: margin,
      bottom: FOOTER_RESERVE,
    },
    // On pages 2+ draw the full header; footer drawn on every page
    didDrawPage: (data) => {
      if (data.pageNumber > 1) {
        drawHeader(doc, formData, logoDataUrl, pageWidth, margin);
      }
      drawFooter();
    },
  });

  y = doc.lastAutoTable.finalY + 14;

  // ══════════════ CHARGES NOTE ══════════════════════════════════════════════
  y = checkPageBreak(y, 24);
  const chargesText = formData.chargesNote || "";
  const chargesW = doc.getTextWidth(chargesText);
  doc.setFillColor(255, 255, 0);
  doc.rect(margin - 2, y - 10, chargesW + 6, 14, "F");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.text(chargesText, margin, y);
  y += 22;

  // ══════════════ TERMS & CONDITIONS ═══════════════════════════════════════
  y = checkPageBreak(y, 30);
  const tcLabel = "Terms & Conditions";
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(192, 57, 43);
  doc.text(tcLabel, margin, y);
  doc.setDrawColor(192, 57, 43);
  doc.setLineWidth(0.5);
  doc.line(margin, y + 2, margin + doc.getTextWidth(tcLabel), y + 2);
  y += 18;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(0, 0, 0);

  (formData.termsAndConditions || []).forEach((term, idx) => {
    const lines = doc.splitTextToSize(`${idx + 1}. ${term}`, contentWidth - 18);
    const blockHeight = lines.length * 13 + 4;
    y = checkPageBreak(y, blockHeight);
    doc.text(lines, margin + 10, y);
    y += blockHeight;
  });

  y += 12;

  // ══════════════ CLOSING NOTE ══════════════════════════════════════════════
  y = checkPageBreak(y, 20);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text(formData.closingNote || "", margin, y);
  y += 20;

  // ══════════════ SIGNATORY ══════════════════════════════════════════════════
  // Estimate total signatory block height to attempt keeping it together;
  // if it doesn't fit as a unit, checkPageBreak on each line individually.
  const signatoryLines = [
    formData.thankYouNote,
    formData.regards,
    formData.signatoryName,
    formData.signatoryDesignation,
    formData.signatoryCompany,
    formData.signatoryAddress,
    formData.signatoryPhone,
  ].filter(Boolean);

  // Try to keep the whole block on one page
  const sigBlockHeight = signatoryLines.length * 14 + 14; // +14 for email line
  y = checkPageBreak(y, sigBlockHeight);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(192, 57, 43);

  signatoryLines.forEach((line) => {
    doc.text(line, margin, y);
    y += 14;
  });

  doc.setTextColor(0, 0, 255);
  doc.text(formData.signatoryEmail || "", margin, y);

  // ── Footer on the last page ──────────────────────────────────────────────
  // autoTable's didDrawPage already drew it on any table-overflow pages;
  // this covers the final page (whether table-only or post-table content).
  drawFooter();

  return doc;
};
